import { Controller } from "@hotwired/stimulus"

/**
 * IoT Override Editor Controller (CONEK-7634)
 *
 * Inline edit for one notification-threshold override row on the admin
 * thresholds page. The row shows its value as text; Edit swaps the display
 * for the form, and the form's submit stays disabled until the typed value
 * differs from the stored one — so "Save" only ever means a real change.
 * Cancel (or Escape) restores the stored value and closes the form.
 *
 * Markup contract (iot/admin/notification_thresholds/index.html.erb):
 *
 *   <div data-controller="iot--override-editor"
 *        data-iot--override-editor-initial-value="12"
 *        data-action="keydown.esc->iot--override-editor#cancel">
 *     <div data-iot--override-editor-target="display">…[Edit]…</div>
 *     <div data-iot--override-editor-target="form" hidden>
 *       <form>
 *         <input data-iot--override-editor-target="input"
 *                data-action="input->iot--override-editor#check">
 *         <button type="submit" data-iot--override-editor-target="submit">
 *         <button type="button" data-action="iot--override-editor#cancel">
 *       </form>
 *     </div>
 *   </div>
 *
 * A create row has no stored value (initial ""), so any non-blank input
 * enables Save. A read-only render has no form target at all; the
 * controller then does nothing.
 *
 * `hidden` is the native HTML attribute: the form must drop out of layout
 * entirely while closed, not merely fade.
 */
export default class extends Controller {
  static targets = ["display", "form", "input", "submit"]
  static values = { initial: { type: String, default: "" } }

  connect() {
    this.close()
  }

  edit(event) {
    if (!this.hasFormTarget) return
    event?.preventDefault()
    this.displayTarget.hidden = true
    this.formTarget.hidden = false
    this.check()
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  cancel(event) {
    if (!this.hasFormTarget || this.formTarget.hidden) return
    event?.preventDefault()
    this.close()
  }

  // Save is a real change or nothing: blank or unchanged keeps it disabled.
  check() {
    if (!this.hasSubmitTarget) return
    const value = this.inputTarget.value.trim()
    this.setSubmitDisabled(value === "" || value === this.initialValue)
  }

  close() {
    if (!this.hasFormTarget) return
    this.inputTarget.value = this.initialValue
    this.formTarget.hidden = true
    this.displayTarget.hidden = false
    if (this.hasSubmitTarget) this.setSubmitDisabled(true)
  }

  // Ui::ButtonComponent renders `disabled` and `aria-disabled` together, so
  // flipping only the property would leave assistive tech (and Playwright)
  // seeing a disabled button. Keep both in step.
  setSubmitDisabled(flag) {
    this.submitTarget.disabled = flag
    this.submitTarget.setAttribute("aria-disabled", String(flag))
  }
}
